import React, {useCallback, useEffect, useState} from 'react';
import {createStyles, makeStyles, withStyles} from '@material-ui/core/styles';
import {lightTheme} from '@components/@theme/lightTheme';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Card from '@material-ui/core/Card';
import TextField from '@material-ui/core/TextField';
import TableContainer from '@material-ui/core/TableContainer';
import moment, {Moment} from 'moment';
import Button from '@material-ui/core/Button';
import {Pagination} from '@components/pagination/Pagination';
import {List} from 'immutable';
import {formatKpiValue, KpiObservation} from '@store/@types/kpi';
import User from 'records/User';
import KpiObservationActions from '@store/kpi/KpiObservationActions';
import {Int} from '@store/@types/util';
import ArrowIconToggle from 'components/pure/utils/icon/ArrowIconToggle';
import {SortDirection, toggleSort} from 'utils/const';
import {parseCurrency} from 'utils/parseCurrency';
import {EditIcon} from '@components/icon/EditIcon';

interface KpiHistoryProps {
  kpiObservations: List<KpiObservation>;
  pageCount: Int;
  search: typeof KpiObservationActions.search;
  update: typeof KpiObservationActions.update;
  startDate: Moment;
  endDate: Moment;
  authUser: User;
  user: User;
}

const PAGE_SIZE = 10;

export const KpiHistory: React.FC<KpiHistoryProps> = ({
  kpiObservations,
  search,
  update,
  pageCount,
  startDate,
  endDate,
  authUser,
  user,
}) => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(SortDirection.DESC);
  // eslint-disable-next-line
  const [searchTerm, setSearchTerm] = useState('');
  const fetchKpis = useCallback(() => {
    if (startDate && endDate) {
      return search({
        count: PAGE_SIZE,
        page: page,
        search: searchTerm,
        startDate,
        endDate,
        userId: user.id,
        sortDirection: sort,
      });
    }
  }, [search, page, searchTerm, startDate, endDate, user?.id, sort]);
  useEffect(() => {
    setTimeout(fetchKpis, 0);
  }, [fetchKpis]);

  const isEditable = useCallback(
    (obs: KpiObservation) => {
      if (obs.created.isAfter(moment().subtract(7, 'days'))) {
        if (user?.id === authUser.id) return true;
        if (user?.managerId === authUser.id) return true;
      }
      return false;
    },
    [authUser, user]
  );

  const arrowOnClick = useCallback(() => {
    setSort(toggleSort(sort));
    setPage(1);
  }, [sort, setSort, setPage]);

  const {arrow, emptyState, tableHead, wrapper, dateCell, paginationWrapper} =
    useStyles(lightTheme);

  return (
    <div className={wrapper}>
      <TextField
        variant="outlined"
        value={searchTerm}
        onChange={e => {
          setPage(1);
          setSearchTerm(e.target.value);
        }}
        placeholder="Search by KPI"
      />
      <Card variant="outlined">
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow className={tableHead}>
                <TableCell>
                  <span>Date</span>
                  <ArrowIconToggle className={arrow} onClick={arrowOnClick} sortDirection={sort} />
                </TableCell>
                <TableCell>KPI</TableCell>
                <TableCell>Entered Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kpiObservations.isEmpty() ? (
                <TableRow>
                  <TableCell align={'center'} colSpan={3} className={emptyState}>
                    {searchTerm ? `No KPIs matching "${searchTerm}"` : `No KPIs provided yet.`}
                  </TableCell>
                </TableRow>
              ) : (
                kpiObservations.map(kpiObservation => (
                  // @ts-ignore TODO resolve
                  <ZebraRow key={kpiObservation.id}>
                    <TableCell className={dateCell}>
                      {kpiObservation.created.format('MM/DD/YYYY')}
                    </TableCell>
                    <TableCell>{kpiObservation.name}</TableCell>
                    <EntryCell
                      kpiObservation={kpiObservation}
                      update={update}
                      fetchKpis={fetchKpis}
                      canEdit={isEditable(kpiObservation)}
                    />
                  </ZebraRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <div className={paginationWrapper}>
        <Pagination color="primary" count={pageCount} page={page} onChange={(e, v) => setPage(v)} />
      </div>
    </div>
  );
};

function EntryCell({
  kpiObservation,
  update,
  fetchKpis,
  canEdit,
}: {
  kpiObservation: KpiObservation;
  update: typeof KpiObservationActions.update;
  fetchKpis: () => any;
  canEdit: boolean;
}) {
  const {editingCell, valueInput, value, valueCell, editIcon} = useStyles(lightTheme);
  const [editMode, setEditMode] = useState(false);
  const [kpiValue, setKpiValue] = useState(kpiObservation.value + '');
  const toggleEditMode = () => setEditMode(!editMode);
  const updateValue = () =>
    update(kpiObservation.id, {value: parseCurrency(kpiValue)})
      .then(fetchKpis)
      .then(toggleEditMode)
      .catch(console.error);

  if (editMode) {
    return (
      <TableCell className={editingCell}>
        <div>
          <TextField
            variant="outlined"
            className={valueInput}
            value={kpiValue}
            onChange={e => setKpiValue(e.target.value)}
          />
          <Button size="small" variant="text" color="primary" onClick={updateValue}>
            Save
          </Button>
          <Button size="small" variant="text" color="primary" onClick={toggleEditMode}>
            Cancel
          </Button>
        </div>
      </TableCell>
    );
  }

  return (
    <TableCell>
      <div className={valueCell}>
        <span className={value}>
          {formatKpiValue(kpiObservation.numericType, kpiObservation.value, {
            forceHours: false,
            includeUnits: true,
          })}
        </span>
        {canEdit && <EditIcon className={editIcon} onClick={toggleEditMode} tooltip="Edit" />}
      </div>
    </TableCell>
  );
}

const ZebraRow = withStyles(theme =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: 'rgba(208, 216, 226, 0.2)',
        '& > *': {
          opacity: 1,
        },
      },
    },
  })
)(TableRow);

const useStyles = makeStyles(theme => ({
  arrow: {
    position: 'absolute',
    left: 50,
    top: 1,
    cursor: 'pointer',
  },
  wrapper: {
    '& > div': {
      marginBottom: '20px',
    },
  },
  emptyState: {
    height: '200px',
  },
  tableHead: {
    backgroundColor: 'rgba(208, 216, 226, 0.2)',
    '& > .MuiTableCell-head': {
      fontWeight: 'bold',
      fontSize: '12px',
      padding: '8px 12px',
      lineHeight: 1,
      position: 'relative',
    },
  },
  valueInput: {
    width: '80px',
  },
  dateCell: {
    width: '90px',
  },
  editingCell: {
    padding: '6px 12px',
    '& > div': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  valueCell: {
    height: '20px',
    width: '210px',
    display: 'flex',
    position: 'relative',
  },
  value: {
    marginRight: '20px',
  },
  editIcon: {
    position: 'absolute',
    right: '120px',
    cursor: 'pointer',
    height: 18,
  },
  paginationWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
}));
